import axios, { AxiosInstance } from 'axios';
import {
  Expense,
  ExpenseCreateRequest,
  Category,
  ApiError,
  ExpenseUpdateRequest,
  ExpensesListResponse
} from '../types';

class ApiService {
  private api: AxiosInstance;
  private controllers: Map<string, AbortController> = new Map();

  constructor() {
    this.api = axios.create({
      baseURL: process.env.REACT_APP_API_URL || '',
      timeout: 5000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add JWT token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        // Handle 401 Unauthorized - token expired or invalid
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          window.location.href = '/login';
        }

        const apiError: ApiError = {
          message: error.response?.data?.detail || error.message || 'Network error',
          status: error.response?.status,
          details: error.response?.data?.details
        };
        throw apiError;
      }
    );
  }

  private createAbortController(key: string): AbortSignal {
    // Cancel previous request with same key
    const existingController = this.controllers.get(key);
    if (existingController) {
      existingController.abort();
    }

    // Create new controller
    const controller = new AbortController();
    this.controllers.set(key, controller);

    return controller.signal;
  }

  private cleanupController(key: string): void {
    this.controllers.delete(key);
  }

  async createExpense(expense: ExpenseCreateRequest): Promise<Expense> {
    const response = await this.api.post('/expenses/manual', expense);
    return response.data;
  }

  async createExpenseFromText(text: string, currency: string = 'USD'): Promise<Expense> {
    const response = await this.api.post('/expenses', { text }, {
      params: { currency }
    });
    return response.data;
  }

  async updateExpense(id: number, expense: ExpenseUpdateRequest, currency: string): Promise<{ expense: Expense; warnings: any[] }> {
    const response = await this.api.put(`/expenses/${id}`, expense, {
      params: { currency }
    });
    return response.data;
  }

  async deleteExpense(id: number): Promise<void> {
    await this.api.delete(`/expenses/${id}`);
  }

  async getCategories(): Promise<Category[]> {
    const signal = this.createAbortController('categories');

    try {
      const response = await this.api.get('/categories', { signal });
      return response.data;
    } finally {
      this.cleanupController('categories');
    }
  }

  async createCategory(name: string, parentId: number | null): Promise<Category> {
    const response = await this.api.post('/categories', {
      name,
      parent_id: parentId
    });
    return response.data;
  }

  async getExpenses(
    currency: string,
    startDate: string,
    endDate: string
  ): Promise<ExpensesListResponse> {
    const signal = this.createAbortController('expenses');

    try {
      const response = await this.api.get('/expenses', {
        params: {
          currency,
          start_date: startDate,
          end_date: endDate
        },
        signal
      });
      return response.data;
    } finally {
      this.cleanupController('expenses');
    }
  }

  async getExpensesByCategory(currency: string, startDate: string, endDate: string): Promise<any> {
    const signal = this.createAbortController('by-category');

    try {
      const response = await this.api.get('/expenses/by-category', {
        params: {
          currency,
          start_date: startDate,
          end_date: endDate
        },
        signal
      });
      return response.data;
    } finally {
      this.cleanupController('by-category');
    }
  }

  async getCategoryTrends(currency: string, startDate?: string, endDate?: string): Promise<any> {
    const signal = this.createAbortController('category-trends');

    try {
      const params: any = { currency };
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const response = await this.api.get('/expenses/category-trends', {
        params,
        signal
      });
      return response.data;
    } finally {
      this.cleanupController('category-trends');
    }
  }

  async getExpenseById(id: number, currency: string, convert: boolean = true): Promise<{ expense: Expense; warnings: any[] }> {
    const response = await this.api.get(`/expenses/${id}`, {
      params: { currency, convert }
    });
    return response.data;
  }

  async getExpenseForEdit(id: number): Promise<Expense> {
    // Fetch expense without conversion - returns original data from DB
    const response = await this.api.get(`/expenses/${id}`, {
      params: { currency: 'USD', convert: 'false' }
    });
    return response.data.expense;
  }

  async getMonthlyExpenses(currency: string, startDate?: string, endDate?: string, excludeBigExpenses: boolean = false): Promise<any> {
    const signal = this.createAbortController('monthly-expenses');

    try {
      const params: any = { currency, exclude_big_expenses: excludeBigExpenses };
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const response = await this.api.get('/expenses/monthly', {
        params,
        signal
      });
      return response.data;
    } finally {
      this.cleanupController('monthly-expenses');
    }
  }

  async getExpenseDateRange(): Promise<{ first_expense_date: string | null; last_expense_date: string | null; has_expenses: boolean }> {
    const response = await this.api.get('/expenses/date-range');
    return response.data;
  }

  // Cancel all pending requests
  cancelAllRequests(): void {
    this.controllers.forEach(controller => {
      controller.abort();
    });
    this.controllers.clear();
  }

  // Cancel specific request by key
  cancelRequest(key: string): void {
    const controller = this.controllers.get(key);
    if (controller) {
      controller.abort();
      this.controllers.delete(key);
    }
  }

  // Authentication endpoints
  async register(email: string, password: string, username?: string): Promise<any> {
    const response = await this.api.post('/auth/register', {
      email,
      password,
      username: username || null,
    });
    return response.data;
  }

  async login(email: string, password: string): Promise<{ access_token: string; token_type: string }> {
    const response = await this.api.post('/auth/login', {
      email,
      password,
    });
    return response.data;
  }

  async getCurrentUser(token: string): Promise<any> {
    const response = await this.api.get('/auth/me', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  }

  async changePassword(oldPassword: string, newPassword: string): Promise<any> {
    const response = await this.api.post('/auth/change-password', {
      old_password: oldPassword,
      new_password: newPassword,
    });
    return response.data;
  }

  async updateUserSettings(settings: { default_currency?: string }): Promise<any> {
    const response = await this.api.patch('/auth/settings', settings);
    return response.data;
  }
}

export const apiService = new ApiService();
export default apiService;
